import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminSupportExtension } from "../../../../../http/patrol/types/supportExtensions";
import { useSupportExtensionsApi } from "../../../../../http/patrol/useSupportExtensions";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateSupportExtensionModalDialogButton from "./CreateSupportExtensionModalDialogButton";
import SupportExtensionForm from "./SupportExtensionForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const SupportExtensions: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "supportExtensions" });
  const aborter = useRef(new AbortController());
  const supportExtensionsApi = useSupportExtensionsApi();
  const { setActions, setTitle } = useSearchContext();

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await supportExtensionsApi.updateAdminSupportExtensionVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToEdit, setItemToEdit] = useState<AdminSupportExtension>();
  const [itemToDelete, setItemToDelete] = useState<AdminSupportExtension>();
  const { accessesAction, accessesDialog } = useEntityAccesses<{ id: string; name?: string }>(
    "patrol.support_extension",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "supportExtensions");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = supportExtensionsApi.useAdminSupportExtensions(query, aborter.current.signal, patrolSystemId);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.supportExtensions.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.supportExtensions.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateSupportExtensionModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminSupportExtension>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.description}</th>
            {!patrolSystemId && <th align="left">{i18n.translation.patrolSystems.singular}</th>}
            <th align="left">{i18n.translation.common.height}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.item.id}`}>
                {item.item.code}
              </Link>
            </td>
            <td>{item.item.description}</td>
            {!patrolSystemId && <td>{item.system.name}</td>}
            <td>{item.height}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.item.id, value)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction({ id: item.item.id, name: item.item.name ?? item.item.code }),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.supportExtensions.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                supportExtensionsApi
                  .deleteAdminSupportExtension(itemToDelete.system.id, itemToDelete.item.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.supportExtensions.questions.delete}
        </ModalDialog>
      )}
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.supportExtensions.edit}>
          <SupportExtensionForm onClose={() => setItemToEdit(undefined)} defaultValues={itemToEdit} />
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default SupportExtensions;
